/**
 * Paramètres de route NUMÉRIQUES (CCT-252).
 *
 * Un écran peut être atteint de deux façons qui ne livrent PAS le même type :
 *   - navigation interne  → `navigate('ChapterDetail', { missionId: 3 })`   → NOMBRE
 *   - URL / deep-link / F5 → `/chapter/?missionId=3`                        → CHAÎNE « 3 »
 *
 * Tout le code écrit en `===` bascule alors en silence : `3 === "3"` est faux, la mission
 * n'est plus trouvée, et l'écran affiche « Mission introuvable » alors qu'il fonctionne
 * parfaitement quand on y arrive par un clic. Le défaut ne se voit donc QUE par URL collée,
 * notification ou rafraîchissement — précisément ce qu'on ne teste pas en cliquant.
 *
 * La conversion de fond se fait dans `navigation/linking.ts` (`parse`), à la frontière où
 * l'URL devient des params. Ce helper est la CEINTURE : il protège les écrans contre un
 * appelant interne qui passerait une chaîne, et contre un chemin d'URL qu'on aurait oublié
 * de déclarer.
 */

/**
 * Ramène une valeur de param à un nombre utilisable, ou `undefined`.
 *
 * `undefined` plutôt que `NaN` : `NaN` se propage en silence (toute comparaison est fausse,
 * toute arithmétique donne `NaN`) et reproduirait exactement le défaut qu'on corrige, en
 * plus difficile à voir. `undefined` fait échouer les gardes existantes (`if (!missionId)`).
 *
 * Une chaîne non numérique (« el-42 ») rend `undefined` : ces identifiants-là sont TEXTUELS
 * et ne doivent jamais être convertis (cf. le couple `el-42` / `42`).
 */
export function numParam(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  // `Number('')` vaut 0 et `Number(' 3 ')` vaut 3 : on exige donc une forme entière stricte,
  // sinon « 3abc » ou « 1e5 » passeraient pour des identifiants légitimes.
  if (!/^-?\d+$/.test(trimmed)) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

/** Idem, avec un repli explicite quand le param est absent ou illisible. */
export function numParamOr(value: unknown, fallback: number): number {
  const n = numParam(value);
  return n === undefined ? fallback : n;
}
